import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { C } from "../lib/colors.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { fmtResolveDate } from "../lib/formatDates.js";
import { listSource } from "../lib/forecast/listSource.js";
import { answerSummary, resolvedVerdict } from "../lib/forecast/answerSummary.js";
import { cadenceLabel, scenarioTriggers } from "../lib/forecast/headerActions.js";
import { pickDepth, readPicks, subscribePicks } from "../lib/forecast/updateDepthPicks.js";
import { isSayerManaged, SAYER_MANAGED_NOTE } from "../lib/featured/observerCopy.js";
import ChangeText from "./ChangeText.jsx";
import TierSelector from "./TierSelector.jsx";
import { Button, Chip, Spinner } from "./ui/Primitives.jsx";
import {
  IconArrowUp,
  IconBranch,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconClose,
  IconCriteria,
  IconPlus,
  IconRefresh,
  IconReport,
  IconShare,
} from "./ui/Icons.jsx";

// The forecast header: the question, then the answer, then what to do about it. It replaces the
// floating question and the right-edge percent as the first thing on screen; the timeline below
// it keeps the detail. Pure props: everything it opens is a handler from AppInner.

/** Update now's depth menu: its toggle, menu and options. */
const UPDATE_DEPTH_IDS = {
  toggle: ui("header.update-depth"),
  menu: ui("header.update-depth-menu"),
  option: ui("header.update-depth-option"),
};

function Dot() {
  return (
    <span aria-hidden="true" style={{ color: C.gray, opacity: 0.6, padding: "0 2px" }}>
      ·
    </span>
  );
}

/** The run state / verdict, as a quiet chip in the meta row. */
function StatusChip({ status }) {
  if (!status) return null;
  const tone =
    status.kind === "resolved"
      ? C.gold
      : status.kind === "error"
        ? C.danger
        : status.kind === "running"
          ? C.accent
          : C.grayL;
  return (
    <Chip
      data-header-status={status.kind}
      // The live loader stands on its own; the other states keep their brackets.
      className={status.kind === "running" ? "sy-chip--bare" : undefined}
      style={{
        color: tone,
        borderColor: `color-mix(in srgb, ${tone} 40%, transparent)`,
        padding: "3px 8px",
      }}
    >
      {status.kind === "running" ? <Spinner /> : status.kind === "resolved" ? <IconCheck /> : null}
      {status.label}
    </Chip>
  );
}

/** "−1 pt from the previous forecast (Oct 14)", coloured by direction (raises / lowers). */
function ChangeChip({ change }) {
  if (!change) return null;
  const tone =
    change.direction === "up" ? C.blueTeam : change.direction === "down" ? C.redTeam : C.gray;
  return (
    <span
      data-header-change={change.direction}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, color: tone, fontSize: 13 }}
    >
      <ChangeText text={change.text} />
      <span style={{ color: C.gray }}>{change.since}</span>
    </span>
  );
}

/** "Called it" / "Missed it": whether Sayer's final forecast was on the side that happened. */
function VerdictChip({ called }) {
  if (called == null) return null;
  const tone = called ? C.blueTeam : C.redTeam;
  return (
    <Chip
      data-header-verdict={called ? "called" : "missed"}
      style={{
        color: tone,
        borderColor: `color-mix(in srgb, ${tone} 45%, transparent)`,
        padding: "3px 8px",
      }}
    >
      {called ? <IconCheck /> : <IconClose />}
      {called ? "Called it" : "Missed it"}
    </Chip>
  );
}

/**
 * A resolved forecast's answer: the outcome in gold where the number was, when it resolved and
 * whether Sayer called it beside it, and Sayer's last forecast under that.
 */
function ResolvedAnswer({ answer, verdict }) {
  const outcome = verdict.outcome || "Resolved";
  const long = outcome.length > 14;
  // Figures in tabular type, words in running text.
  const num = (t) => <span className="sy-num">{t}</span>;
  const lastForecast =
    answer.kind === "binary" ? (
      <>
        {num(answer.headlineText)} {answer.headlineLabel}
      </>
    ) : verdict.outcome ? (
      <>
        {verdict.outcome} at {num(verdict.pText)}
        {verdict.rank ? <>, ranked #{num(verdict.rank)}</> : null}
      </>
    ) : (
      answer.leaderStatement
    );
  return (
    <div
      data-header-answer="resolved"
      style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 14, minWidth: 0 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minWidth: 0,
          maxWidth: "55%",
          color: C.gold,
          fontSize: long ? 30 : 44,
          fontWeight: 600,
          lineHeight: 1.05,
        }}
      >
        <IconCheck
          width={long ? 26 : 34}
          height={long ? 26 : 34}
          strokeWidth={2.5}
          style={{ flexShrink: 0 }}
        />
        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {outcome}
        </span>
      </div>
      <span
        style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", minWidth: 0 }}
      >
        {lastForecast && (
          <span style={{ fontSize: 19, color: C.white, fontWeight: 600, lineHeight: 1.3 }}>
            Sayer&apos;s last forecast: {lastForecast}
          </span>
        )}
        <VerdictChip called={verdict.called} />
      </span>
    </div>
  );
}

/** "Oct 14, 2026, 3:42 PM"; "" for a missing or bad date. */
function fmtUpdated(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// The action row's spacing: between its two groups, and before Resolution Criteria.
const GROUP_GAP = 56;
const ROW_GAP = 8;

/** A flex row's width with every child at its own size, side by side. */
function naturalWidth(el) {
  const kids = [...el.children];
  const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
  return kids.reduce((sum, k) => sum + k.offsetWidth, 0) + gap * Math.max(0, kids.length - 1);
}

/**
 * How much of the action row fits on one line, as things give way when a panel narrows the
 * header: 0 everything; 1 without Resolution Criteria; 2 also without the "Updated" date. Past
 * that the groups wrap.
 */
function useActionRowFit(rowRef, groupsRef, criteriaRef, updatedRef, layoutKey, showUpdated) {
  const [level, setLevel] = useState(0);
  // Each part's width while shown, so the row knows when there is room to bring it back.
  const criteriaW = useRef(0);
  const updatedW = useRef(0);
  useEffect(() => {
    const row = rowRef.current;
    const groups = groupsRef.current;
    if (!row || !groups || typeof ResizeObserver === "undefined") return undefined;
    // The observer reports once on observing, so no measuring call is needed here.
    const ro = new ResizeObserver(() => {
      if (criteriaRef.current) criteriaW.current = criteriaRef.current.offsetWidth;
      const updated = updatedRef.current;
      if (updated) {
        updatedW.current =
          updated.offsetWidth +
          (parseFloat(getComputedStyle(updated.parentElement).columnGap) || 0);
      }
      const parts = [...groups.children];
      const groupsNow =
        parts.reduce((sum, g) => sum + naturalWidth(g), 0) +
        GROUP_GAP * Math.max(0, parts.length - 1);
      const lean = groupsNow - (updated ? updatedW.current : 0);
      const avail = row.clientWidth;
      const dateWidth = showUpdated ? updatedW.current : 0;
      const next =
        lean + dateWidth + ROW_GAP + criteriaW.current <= avail
          ? 0
          : lean + dateWidth <= avail
            ? 1
            : 2;
      setLevel((cur) => (cur === next ? cur : next));
    });
    ro.observe(row);
    for (const g of groups.children) ro.observe(g);
    return () => ro.disconnect();
  }, [rowRef, groupsRef, criteriaRef, updatedRef, layoutKey, showUpdated]);
  return level;
}

/** "Oct 16, 2026 · 3:12 PM", for the line beside the update controls. */
function fmtUpdatedShort(iso) {
  const d = new Date(iso || "");
  if (Number.isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day} · ${time}`;
}

/** The big number: "18" with a smaller "%". */
function HeroNumber({ text }) {
  const m = /^(.*?)(%)$/.exec(text || "");
  return (
    <div
      className="sy-num"
      data-header-hero=""
      style={{
        fontSize: 52,
        fontWeight: 600,
        lineHeight: 0.92,
        letterSpacing: "-0.05em",
        color: C.white,
        whiteSpace: "nowrap",
      }}
    >
      {m ? m[1] : text}
      {m && <span style={{ letterSpacing: 0, color: C.grayL, marginLeft: 2 }}>%</span>}
    </div>
  );
}

/** A day as a date field holds it ("2026-10-27"), in the viewer's own calendar. */
function localDay(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const FIELD = {
  background: C.inputBg,
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  color: C.white,
  fontFamily: "inherit",
  fontSize: 13,
  padding: "6px 8px",
  outline: "none",
  boxSizing: "border-box",
};

/**
 * "Explore a scenario": the upcoming triggers that would move the answer most, then one line for
 * an event of the user's own and its day, explored on Enter as the timeline's double-click
 * popover explores one. What is typed stays while the menu closes and reopens on the same
 * forecast.
 */
function ScenarioMenu({ options, onPick, today, onExploreCustom }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [day, setDay] = useState(() => localDay(today));
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    // telemetry-gesture: closes the scenario menu; no operation
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const none = options.length === 0 && !onExploreCustom;
  const exploreOnEnter = (e) => {
    // Enter while an input method is composing (229: the composing key code) confirms the text.
    const composing = e.nativeEvent.isComposing || e.keyCode === 229;
    if (e.key !== "Enter" || composing || !description.trim() || !day) return;
    setOpen(false);
    setDescription("");
    onExploreCustom(description.trim(), day);
  };
  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <Button
        {...ui("header.explore-scenario")}
        icon={<IconBranch />}
        iconRight={<IconChevronDown />}
        disabled={none}
        title={
          none
            ? "No upcoming triggers to explore"
            : "See how the answer changes if something happens"
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Explore a scenario
      </Button>
      {open && (
        // telemetry-unsurfaced: drawn inside the header element, so it stays inside its surface.
        <div
          role="dialog"
          aria-label="Explore a scenario"
          data-header-scenarios=""
          // The options read as trigger titles (model text): never their names in click capture.
          data-telemetry-redact=""
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            width: 360,
            padding: 6,
            background: C.tooltipBg,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            boxShadow: C.shadowBox,
            zIndex: 5,
          }}
        >
          {options.length > 0 && (
            <div style={{ padding: "6px 10px 8px", fontSize: 12, color: C.gray }}>
              What if one of these happens?
            </div>
          )}
          {options.map(({ ev, when, pEvent }) => (
            <button
              key={ev.id ?? ev.title}
              type="button"
              {...ui("header.scenario-option", ev.id)}
              onClick={() => {
                setOpen(false);
                onPick(ev);
              }}
              className="sy-header-option"
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "8px 10px",
                border: "none",
                borderRadius: 8,
                background: "transparent",
                color: C.white,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <span
                style={{
                  display: "block",
                  fontSize: 13,
                  lineHeight: 1.35,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ev.title}
              </span>
              <span
                className="sy-num"
                style={{ display: "block", marginTop: 2, fontSize: 12, color: C.gray }}
              >
                {when ? fmtResolveDate(when) : "Date not set"}
                {pEvent != null ? ` · ${Math.round(pEvent * 100)}% likely` : ""}
              </span>
            </button>
          ))}
          {onExploreCustom && (
            <div
              data-header-custom-scenario=""
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 2px 2px",
                ...(options.length > 0 && {
                  marginTop: 4,
                  paddingTop: 8,
                  borderTop: `1px solid ${C.border}`,
                }),
              }}
            >
              <input
                {...ui("header.scenario-custom-description")}
                aria-label="Enter your own event"
                placeholder="Enter your own event"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={exploreOnEnter}
                className="pro-input"
                style={{ ...FIELD, flex: 1, minWidth: 0 }}
              />
              <input
                {...ui("header.scenario-custom-date")}
                type="date"
                aria-label="When it happens"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                onKeyDown={exploreOnEnter}
                className="pro-input"
                style={{ ...FIELD, flex: "none", fontSize: 12, padding: "6px" }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The header for the active forecast: question, meta, the answer, and the actions.
 *
 * @param {object} props
 * @param {object} props.forecast the DISPLAYED run (scrub-aware), for the answer
 * @param {object|null} [props.previous] the version to compare with (a past version's own
 *   predecessor); undefined derives it from `forecast`
 * @param {object} props.run the live run, for engine and dates
 * @param {string} props.title the question text
 * @param {string} props.inset left padding that clears an open rail panel (a CSS length)
 * @param {(el: HTMLElement|null) => void} [props.measureRef] reports the header's height
 * @param {string|null} props.updatedAt the latest forecast moment; while `updateNow.running`, the
 *   moment of the answer on screen (the version before the run), which holds the date's place unseen
 * @param {string|null} props.viewingAt the scrubbed vintage's moment, when scrubbed
 * @param {number} props.nowMs the app's "today"
 * @param {boolean} props.questionOpen whether the Question panel is open
 * @param {() => void} props.onOpenQuestion the title click: opens, never closes, the panel
 * @param {() => void} [props.onToggleQuestion] the "Resolution Criteria" button: opens or closes it
 * @param {boolean} [props.questionBlink] a fresh question: the button blinks, as the rail item did
 * @param {boolean} props.reportEnabled
 * @param {() => void} props.onOpenReport
 * @param {(ev: object) => void} props.onExploreTrigger opens the trigger's scenario dialog
 * @param {(description: string, day: string) => void} [props.onExploreCustom] branches on an
 *   event the user typed, as the timeline's double-click popover does; absent, the menu offers
 *   only the upcoming triggers
 * @param {(() => void)|undefined} props.onShare absent where sharing is off
 * @param {number|null} props.cadenceIdx the tracking cadence, or null where tracking is off
 * @param {() => void} props.onOpenUpdates
 * @param {{visible: boolean, enabled: boolean, reason: string|null, runAsMine?: boolean,
 *   running?: boolean}} props.updateNow
 *   `runAsMine`: the slot offers a forecast of the viewer's own instead (another org's forecast);
 *   `running`: a run is in progress, shown in the slot in place of the status chip
 * @param {(tier?: string) => void} props.onUpdateNow runs an update: at the depth picked from its
 *   menu, or with none at `updateDepth`
 * @param {string|null} [props.updateDepth] the depth an update runs at unless another is picked
 * @param {string[]} [props.updateDepthOptions] the depths to offer; with fewer than two, no menu
 * @param {(() => void)|undefined} [props.onRunAsMine] runs the question as the viewer's own
 *   forecast; absent, the `runAsMine` slot is not drawn
 * @param {boolean} [props.minimal] a first run is in progress and its status card is showing:
 *   only the question and its dates, no answer, no actions, no status chip
 * @param {object|null} [props.answerCheck] the search for a closed question's answer
 *   (lib/forecast/answerCheck.js answerCheckState), which names the status chip; null while the
 *   question is open or answered. Its panel sits on the timeline, right of the outcomes.
 */
export default function ForecastHeader({
  forecast,
  previous,
  run,
  title,
  minimal = false,
  inset = "0px",
  measureRef,
  updatedAt,
  viewingAt,
  nowMs,
  questionOpen = false,
  onOpenQuestion,
  onToggleQuestion,
  questionBlink = false,
  reportEnabled,
  onOpenReport,
  onExploreTrigger,
  onExploreCustom,
  onShare,
  cadenceIdx,
  onOpenUpdates,
  updateNow,
  onUpdateNow,
  updateDepth = null,
  updateDepthOptions = [],
  onRunAsMine,
  answerCheck = null,
}) {
  const actionRowRef = useRef(null);
  const actionGroupsRef = useRef(null);
  const criteriaRef = useRef(null);
  const updatedRef = useRef(null);
  // A picked depth runs only when Update now is pressed.
  const pickedDepth = useSyncExternalStore(subscribePicks, readPicks, readPicks)[
    run?.id ?? forecast?.id
  ];
  const showUpdateNow = !!updateNow?.visible && (!updateNow.runAsMine || !!onRunAsMine);
  const running = showUpdateNow && !!updateNow.running;
  // Which controls the row holds; a change re-measures it.
  const layoutKey = [
    !!forecast && !!title,
    minimal,
    !!updatedAt,
    forecast?.status,
    !!forecast?._pending,
    cadenceIdx,
    showUpdateNow,
    !!updateNow?.runAsMine,
    !!updateNow?.running,
    pickedDepth ?? updateDepth,
    updateDepthOptions.length,
    !!onShare,
  ].join("|");
  const now = new Date(nowMs);
  const answer = forecast ? answerSummary(listSource(forecast), { now, previous }) : null;
  // A rerun's start stamp also drives cooldown and chat ordering. It cannot describe
  // the displayed answer until the rerun succeeds, so suppress it for unfinished attempts. While
  // Update now holds the run, the date keeps its place unseen, so Update now does not move.
  const showUpdated =
    !minimal &&
    updatedAt &&
    (running || (answer?.status?.kind !== "running" && answer?.status?.kind !== "error"));
  const rowFit = useActionRowFit(
    actionRowRef,
    actionGroupsRef,
    criteriaRef,
    updatedRef,
    layoutKey,
    showUpdated,
  );
  const criteriaFits = rowFit === 0;
  // Below level 2 the row fits on one line by the fit's own measure, so it never wraps there: the
  // frame a narrowing (a rail panel sliding in) paints before the fit catches up overflows for
  // that frame instead of wrapping, which would bounce the header's height under the timeline.
  const rowWrap = rowFit >= 2 ? "wrap" : "nowrap";
  if (!forecast || !title || typeof nowMs !== "number") return null;
  const scenarios = scenarioTriggers(forecast.events, { today: now });
  // A copy of one of Sayer's forecasts keeps Sayer's schedule.
  const managed = isSayerManaged(run ?? forecast);
  const cadence = cadenceLabel(run ?? forecast, cadenceIdx);
  const id = run?.id ?? forecast.id;
  const split = updateDepthOptions.length > 1;
  // A run in progress is drawn over Update now's own contents, which keep the box's size.
  const busy = running && (
    <span className="sy-busy__label" title={updateNow.reason || undefined}>
      <Spinner />
      Forecasting
    </span>
  );
  const updateNowButton = (
    <Button
      {...ui("timeline.reforecast", id)}
      className={running && !split ? "sy-busy" : undefined}
      icon={<IconRefresh />}
      disabled={!updateNow?.enabled}
      title={updateNow?.reason || undefined}
      onClick={() => onUpdateNow(pickedDepth)}
    >
      Update now
      {!split && busy}
    </Button>
  );
  const hasAnswer = !minimal && answer.kind !== "none" && answer.headlineText;
  const verdict = minimal ? null : resolvedVerdict(answer);
  // A run in progress shows in Update now's place rather than under the question.
  const shown =
    !minimal && !verdict && !(running && answer.status?.kind === "running") ? answer.status : null;
  const status =
    shown && answerCheck && shown.kind === "pending"
      ? { ...shown, label: answerCheck.label }
      : shown;

  return (
    <header
      ref={measureRef}
      data-forecast-header=""
      {...surface("forecast-header", id)}
      style={{
        flexShrink: 0,
        position: "relative",
        // Above the timeline's sticky line labels and full-height hit strips (z14–16), which
        // are siblings in this column, and below the rail panels (z50+) that overlay it.
        zIndex: 20,
        boxSizing: "border-box",
        padding: "18px 24px 14px",
        paddingLeft: `calc(${inset} + 24px)`,
        transition: "padding-left var(--rail-slide)",
        background: C.bg,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <style>{`.sy-header-option:hover{background:${C.rowActive} !important}`}</style>
      <div>
        <button
          {...ui("question.open-panel")}
          data-telemetry-redact=""
          type="button"
          title={title}
          aria-expanded={questionOpen}
          onClick={onOpenQuestion}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            maxWidth: "100%",
            padding: 0,
            margin: 0,
            border: "none",
            background: "none",
            cursor: "pointer",
            textAlign: "left",
            fontFamily: "inherit",
            color: C.white,
            fontSize: 22,
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: 0,
          }}
        >
          {title}
        </button>

        {(viewingAt || status) && (
          <div
            data-header-meta=""
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 24,
              rowGap: 4,
              marginTop: 8,
              fontSize: 14,
              color: C.gray,
            }}
          >
            {/* When it was last updated sits with the update controls below. */}
            {viewingAt && (
              <span>
                Viewing{" "}
                <span style={{ color: C.grayL }}>the forecast from {fmtUpdated(viewingAt)}</span>
              </span>
            )}
            {status && <StatusChip status={status} />}
          </div>
        )}

        {verdict && <ResolvedAnswer answer={answer} verdict={verdict} />}

        {hasAnswer && !verdict && (
          <div
            data-header-answer={answer.kind}
            style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 14 }}
          >
            <HeroNumber text={answer.headlineText} />
            {/* The full distribution lives at the right of the timeline; the header names the
                leading outcome only. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 19, color: C.white, fontWeight: 600, lineHeight: 1.3 }}>
                {answer.kind === "binary" ? answer.headlineLabel : answer.leaderStatement}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {answer.kind === "multi" && answer.otherLarger && (
                  <span style={{ fontSize: 13, color: C.grayL }}>
                    Outcomes not listed hold more
                  </span>
                )}
                <ChangeChip change={answer.change} />
              </span>
            </div>
          </div>
        )}

        {!minimal && (
          // "Resolution Criteria" holds the row's right end while there is room, and steps aside first.
          <div
            ref={actionRowRef}
            style={{ display: "flex", alignItems: "flex-start", gap: ROW_GAP, marginTop: 14 }}
          >
            {/* Two groups that wrap as wholes: the forecast's own actions, then when it was updated
            and how it keeps updating. */}
            <div
              ref={actionGroupsRef}
              data-header-actions=""
              style={{
                display: "flex",
                flexWrap: rowWrap,
                alignItems: "center",
                gap: `10px ${GROUP_GAP}px`,
                flex: 1,
                minWidth: 0,
              }}
            >
              <div style={{ display: "flex", flexWrap: rowWrap, alignItems: "center", gap: 8 }}>
                <Button
                  variant="primary"
                  {...ui("header.open-report", id)}
                  icon={<IconReport />}
                  disabled={!reportEnabled}
                  title={
                    reportEnabled
                      ? "Open the full report"
                      : "The report is ready when the forecast finishes"
                  }
                  onClick={onOpenReport}
                >
                  Open report
                </Button>
                <ScenarioMenu
                  // A scenario typed for one forecast never carries over to the next.
                  key={id}
                  options={scenarios}
                  onPick={onExploreTrigger}
                  today={nowMs}
                  onExploreCustom={onExploreCustom}
                />
                {onShare && (
                  <Button {...ui("header.share", id)} icon={<IconShare />} onClick={onShare}>
                    Share
                  </Button>
                )}
              </div>
              {(showUpdated || cadence || showUpdateNow) && (
                <div
                  data-header-updates=""
                  style={{ display: "flex", alignItems: "center", gap: 12 }}
                >
                  {showUpdated && rowFit < 2 && (
                    <span
                      ref={updatedRef}
                      aria-hidden={running || undefined}
                      style={{
                        fontSize: 13,
                        color: C.gray,
                        whiteSpace: "nowrap",
                        visibility: running ? "hidden" : undefined,
                      }}
                    >
                      Updated {fmtUpdatedShort(updatedAt)}
                    </span>
                  )}
                  {/* The schedule and Update now read as one control: two halves of one box. */}
                  <div className="sy-btn-pair" style={{ display: "inline-flex" }}>
                    {cadence && (
                      <Button
                        {...ui("header.updates", id)}
                        icon={<IconClock />}
                        iconRight={<IconChevronDown />}
                        // With no room for the date beside it, the date moves into its tooltip.
                        title={
                          (showUpdated && !running && rowFit >= 2
                            ? `Updated ${fmtUpdatedShort(updatedAt)}. `
                            : "") +
                          (managed
                            ? `${SAYER_MANAGED_NOTE} Alert settings`
                            : "Update and alert settings")
                        }
                        onClick={onOpenUpdates}
                      >
                        {cadence}
                      </Button>
                    )}
                    {showUpdateNow &&
                      (updateNow.runAsMine ? (
                        <Button
                          {...ui("header.run-as-mine", id)}
                          icon={<IconPlus />}
                          title={updateNow.reason || undefined}
                          onClick={onRunAsMine}
                        >
                          Forecast this question myself
                        </Button>
                      ) : split ? (
                        // The depth sits inside Update now's box: it is how that update runs. The
                        // box carries the reason it is greyed, since a disabled button may show no
                        // tooltip; its disabled halves let the pointer through to it.
                        <div
                          className={
                            running ? "sy-btn sy-btn-split sy-busy" : "sy-btn sy-btn-split"
                          }
                          title={updateNow.enabled ? undefined : updateNow.reason || undefined}
                        >
                          {updateNowButton}
                          <TierSelector
                            // A new menu each time Update now turns on or off, so one left open
                            // as an update starts is shut when Update now returns.
                            key={updateNow.enabled ? "on" : "off"}
                            variant="inset"
                            tiers={updateDepthOptions}
                            current={pickedDepth ?? updateDepth}
                            onSelect={(tier) => pickDepth(id, tier)}
                            C={C}
                            placement="below"
                            heading="Update now at"
                            title={
                              updateNow.enabled
                                ? "Choose the depth Update now runs at"
                                : updateNow.reason || undefined
                            }
                            disabled={!updateNow.enabled}
                            ids={UPDATE_DEPTH_IDS}
                          />
                          {busy}
                        </div>
                      ) : (
                        updateNowButton
                      ))}
                  </div>
                </div>
              )}
            </div>
            {onToggleQuestion && criteriaFits && (
              <Button
                variant="ghost"
                ref={criteriaRef}
                {...ui("header.resolution-criteria", id)}
                icon={<IconCriteria />}
                aria-pressed={questionOpen}
                className={questionBlink ? "contract-blink" : undefined}
                title="How this question resolves: its wording, criteria and dates"
                onClick={onToggleQuestion}
                style={{ flex: "none" }}
              >
                Resolution Criteria
              </Button>
            )}
          </div>
        )}
        {!minimal && managed && (
          <div data-header-managed="" style={{ marginTop: 8, fontSize: 13, color: C.gray }}>
            {SAYER_MANAGED_NOTE}
          </div>
        )}
      </div>
    </header>
  );
}
